// Seller-rules batch — Verkäufer-Aktivität compliance page.
//
// Table of all obligated sellers (status=potpisan_ugovor with an armed
// activity_due_at): seller, makler, second makler, last qualifying note,
// deadline, days overdue — plus the compliance remedies (Verwarnung /
// Verlängern) and a manual "Prüfung jetzt ausführen" trigger that mirrors
// the 03:00-Vienna cron (POST /clients/compliance/run).
// Gate: admin role + (profiles.seller_compliance OR super admin admin1) —
// same predicate in beforeLoad and the _app.tsx nav entry.

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, CalendarClock, Eye, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useComplianceOverview,
  useComplianceWarn,
  useComplianceExtend,
  useRunComplianceCheck,
  type SellerActivityRow,
} from '@/hooks/data/useSellerActivity';
import { useClient } from '@/hooks/data/useClients';
import { KundendetailsDialog } from '@/components/clients/KundendetailsDialog';
import { humanizeApiError } from '@/lib/errorMessages';
import { fetchMe } from '@/lib/auth';
import { formatDate } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/admin/seller-activity')({
  beforeLoad: async () => {
    const me = await fetchMe();
    const p = me?.profile;
    const allowed =
      p?.role === 'admin' &&
      (p?.seller_compliance === true || p?.agent_id === 'admin1');
    if (!allowed) throw redirect({ to: '/dashboard' });
  },
  component: SellerActivityPage,
});

const DAY_MS = 86_400_000;
// Documented pitfall: timestamp_ms columns serialize as ISO STRINGS.
const toMs = (v: number | string | null | undefined): number | null =>
  v == null ? null : typeof v === 'number' ? v : new Date(v).getTime();

function SellerActivityPage() {
  const { t } = useTranslation();
  const { data: rows = [], isLoading } = useComplianceOverview();
  const runCheck = useRunComplianceCheck();
  const warn = useComplianceWarn();
  const [extendRow, setExtendRow] = useState<SellerActivityRow | null>(null);
  const [viewClientId, setViewClientId] = useState<string | null>(null);
  const { data: viewClient } = useClient(viewClientId ?? undefined);

  const now = Date.now();
  const errFallback = t('errors.general', 'Die Aktion ist fehlgeschlagen.');

  const sellerName = (r: SellerActivityRow) =>
    [r.title, r.first_name, r.last_name].filter(Boolean).join(' ') || '—';

  const onRun = () => {
    runCheck.mutate(undefined, {
      onSuccess: (res) =>
        toast.success(
          t(
            'sellerActivity.run_done',
            'Prüfung abgeschlossen: {{missed}} versäumt, {{advanced}} verlängert, {{disarmed}} beendet.',
            { missed: res.missed, advanced: res.advanced, disarmed: res.disarmed },
          ),
        ),
      onError: (e) =>
        toast.error(humanizeApiError((e as Error).message, t, errFallback)),
    });
  };

  const onWarn = (r: SellerActivityRow) => {
    if (
      !window.confirm(
        t(
          'sellerActivity.warn_confirm',
          '{{name}}: Verwarnung an die zuständigen Makler senden?',
          { name: sellerName(r) },
        ),
      )
    )
      return;
    warn.mutate(
      { id: r.client_id },
      {
        onSuccess: () =>
          toast.success(t('sellerActivity.warn_done', 'Verwarnung gesendet.')),
        onError: (e) =>
          toast.error(humanizeApiError((e as Error).message, t, errFallback)),
      },
    );
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('sellerActivity.title', 'Verkäufer-Aktivität')}
          </h2>
          <p className="text-sm text-ink-3">
            {t(
              'sellerActivity.subtitle',
              'Aktivitätspflicht nach Vertragsunterschrift: mindestens 1 Notiz pro 10 Tage. Nur Compliance-Verantwortliche.',
            )}
          </p>
        </div>
        <Button type="button" onClick={onRun} disabled={runCheck.isPending}>
          <Play className="h-3.5 w-3.5" />
          {runCheck.isPending
            ? t('sellerActivity.running', 'Prüfung läuft …')
            : t('sellerActivity.run_now', 'Prüfung jetzt ausführen')}
        </Button>
      </header>

      <div className="fm-card p-0 overflow-hidden">
        <div className="fm-table-wrap">
          <table className="w-full text-start text-sm">
            <thead>
              <tr className="border-b border-line text-[11px] uppercase tracking-[0.06em] text-ink-3">
                <th className="px-3 py-2">{t('sellerActivity.col.seller', 'Verkäufer')}</th>
                <th className="px-3 py-2">{t('sellerActivity.col.makler', 'Makler')}</th>
                <th className="px-3 py-2">{t('sellerActivity.col.secondary', 'Zweiter Makler')}</th>
                <th className="px-3 py-2">{t('sellerActivity.col.last_note', 'Letzte Notiz')}</th>
                <th className="px-3 py-2">{t('sellerActivity.col.due', 'Fällig am')}</th>
                <th className="px-3 py-2">{t('sellerActivity.col.overdue', 'Überfällig')}</th>
                <th className="px-3 py-2 text-end">{t('sellerActivity.col.actions', 'Aktionen')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-ink-3">
                    {t('sellerActivity.loading', 'Wird geladen …')}
                  </td>
                </tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-ink-3">
                    {t(
                      'sellerActivity.empty',
                      'Keine überwachten Verkäufer — derzeit ist keine Aktivitätspflicht offen.',
                    )}
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const dueMs = toMs(r.activity_due_at);
                const lastMs = toMs(r.last_note_at);
                const dueValid = dueMs != null && !Number.isNaN(dueMs);
                const overdueDays =
                  dueValid && dueMs! <= now
                    ? Math.floor((now - dueMs!) / DAY_MS)
                    : 0;
                return (
                  <tr key={r.client_id} className="border-b border-line last:border-b-0">
                    <td className="px-3 py-2 font-medium text-ink whitespace-nowrap">
                      {sellerName(r)}
                    </td>
                    <td className="px-3 py-2 text-ink-2 whitespace-nowrap">
                      {r.assigned_agent_name ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-ink-2 whitespace-nowrap">
                      {r.secondary_agent_name ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-ink-2 whitespace-nowrap">
                      {lastMs != null && !Number.isNaN(lastMs)
                        ? formatDate(new Date(lastMs))
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-ink-2 whitespace-nowrap">
                      {dueValid ? formatDate(new Date(dueMs!)) : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {overdueDays > 0 ? (
                        <span className="fm-pill bg-destructive/10 text-destructive">
                          {t('sellerActivity.overdue_days', '{{n}} Tage', {
                            n: overdueDays,
                          })}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <div className="inline-flex flex-nowrap items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => onWarn(r)}
                          disabled={warn.isPending}
                          className="inline-flex h-7 items-center gap-1 rounded-md border border-line bg-card px-2 text-xs text-ink-2 hover:bg-paper-2 disabled:opacity-50"
                        >
                          <Bell className="h-3 w-3" />
                          {t('sellerActivity.action.warn', 'Verwarnung senden')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setExtendRow(r)}
                          className="inline-flex h-7 items-center gap-1 rounded-md border border-line bg-card px-2 text-xs text-ink-2 hover:bg-paper-2"
                        >
                          <CalendarClock className="h-3 w-3" />
                          {t('sellerActivity.action.extend', 'Verlängern')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewClientId(r.client_id)}
                          title={t('sellerActivity.action.open', 'Öffnen')}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
                        >
                          <Eye className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {extendRow && (
        <ExtendDialog
          row={extendRow}
          open
          onOpenChange={(o) => !o && setExtendRow(null)}
        />
      )}
      {viewClient && viewClientId && (
        <KundendetailsDialog
          client={viewClient}
          open
          onOpenChange={(o) => !o && setViewClientId(null)}
        />
      )}
    </section>
  );
}

function ExtendDialog({
  row,
  open,
  onOpenChange,
}: {
  row: SellerActivityRow;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation();
  const extend = useComplianceExtend();
  const [preset, setPreset] = useState<number | null>(30);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const until = date
    ? new Date(`${date}T12:00:00`).getTime()
    : preset != null
      ? Date.now() + preset * DAY_MS
      : null;
  const untilValid = until != null && !Number.isNaN(until);
  const reasonOk = reason.trim().length >= 5;

  const submit = () => {
    setErr(null);
    if (!untilValid || !reasonOk) return;
    extend.mutate(
      { id: row.client_id, until: until!, reason: reason.trim() },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success(t('sellerActivity.extend.done', 'Frist verlängert.'));
        },
        onError: (e) =>
          setErr(
            humanizeApiError(
              (e as Error).message,
              t,
              t('errors.general', 'Die Aktion ist fehlgeschlagen.'),
            ),
          ),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('sellerActivity.extend.title', 'Frist verlängern')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-ink-3">
            {t(
              'sellerActivity.extend.hint',
              'Neue Fälligkeit für die Aktivitätspflicht setzen (z. B. Verkäufer im Urlaub). Der Grund wird im Verlauf gespeichert.',
            )}
          </p>
          <div>
            <Label>{t('sellerActivity.extend.preset_label', 'Verlängern um')}</Label>
            <div className="mt-1 flex gap-2">
              {[15, 30, 60].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    setPreset(n);
                    setDate('');
                  }}
                  className={
                    preset === n && !date
                      ? 'rounded-md border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold))]/10 px-3 py-1.5 text-xs font-semibold text-ink'
                      : 'rounded-md border border-line px-3 py-1.5 text-xs text-ink-2 hover:bg-paper-2'
                  }
                >
                  {t('sellerActivity.extend.days', '+{{n}} Tage', { n })}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('sellerActivity.extend.date_label', 'Oder Datum wählen')}</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>
              {t('sellerActivity.extend.reason_label', 'Grund (Pflicht, min. 5 Zeichen)')} *
            </Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t(
                'sellerActivity.extend.reason_ph',
                'z. B. Verkäufer bis 15.08. im Urlaub',
              )}
            />
          </div>
          {err && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {err}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <Button
            type="button"
            disabled={extend.isPending || !reasonOk || !untilValid}
            onClick={submit}
          >
            {t('sellerActivity.extend.submit', 'Verlängern')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
